import { RequestManagedCluster } from "@/components/DevicePage/RequestManagedCluster/RequestManagedCluster";
import { Button } from "@/components/ui/button";
import { INVENTORY_REGIONS } from "@/constants";
import { AnalyticsEventType } from "@/constants/analytics";
import { useUserStore } from "@/store/user";
import {
  ClusterType,
  DynamicSupplyHardwareFiltersResponse,
  RequestManagedClusterFormType
} from "@/types";
import { trackEvent } from "@/utils/analytics";
import { getClusterTypeDisplayName, submitHardwareRequestToHubSpot } from "@/utils/cluster";
import { useState } from "react";

export const HardwareGridEmpty: React.FC<{
  clusterType: ClusterType;
  searchValue: string;
  selectedFilters: Partial<DynamicSupplyHardwareFiltersResponse> | null;
  description: string;
  onResetFilters: () => void;
}> = ({ clusterType, searchValue, selectedFilters, description, onResetFilters }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const theme = useUserStore((state) => state.theme);
  const fillDark = theme === "light" ? "#fff" : "#0D0D0D";
  const fillMedium = theme === "light" ? "#fff" : "#121212";

  const selectedHardwareNames: string[] = [];
  if (searchValue) {
    selectedHardwareNames.push(searchValue);
  }
  if (selectedFilters?.gpu_family) {
    selectedHardwareNames.push(...(selectedFilters?.gpu_family as string[]));
  }
  return (
    <>
      <div className="flex h-[532px] w-full flex-col items-center justify-center gap-6 rounded-[8px] border border-gray-dark-200">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="193"
          height="89"
          viewBox="0 0 193 89"
          fill="none"
        >
          <rect
            x="0.336914"
            y="18.6714"
            width="8.79995"
            height="17.5999"
            rx="0.538462"
            fill={fillDark}
            stroke="#1F1F1F"
            strokeWidth="0.673077"
          />
          <path
            d="M102.272 37.0029L118.819 3.90827C119.914 1.71921 122.151 0.336426 124.598 0.336426L180.143 0.336426C183.712 0.336426 186.605 3.22936 186.605 6.79796V81.8744C186.605 85.443 183.712 88.3359 180.143 88.3359H124.598C122.151 88.3359 119.914 86.9531 118.819 84.7641L102.272 51.6695L85.7245 84.7641C84.63 86.9531 82.3926 88.3359 79.9451 88.3359H24.4005C20.8319 88.3359 17.939 85.443 17.939 81.8744L17.939 6.79797C17.939 3.22936 20.8319 0.336426 24.4005 0.336426L79.9451 0.336426C82.3926 0.336426 84.63 1.71921 85.7245 3.90828L102.272 37.0029Z"
            fill={fillMedium}
            stroke="#1F1F1F"
            strokeWidth="0.673077"
          />
          <path
            d="M74.6367 5.20166C73.1461 2.22047 70.0994 0.337021 66.7663 0.336914L15.005 0.336914C10.145 0.336914 6.20508 4.27679 6.20508 9.13686L6.20508 79.5365C6.20508 84.3965 10.1449 88.3364 15.005 88.3364H66.7663C70.0994 88.3363 73.1461 86.4529 74.6367 83.4717L76.788 79.1698H104.288L106.439 83.4717C107.93 86.4529 110.976 88.3363 114.31 88.3364H166.071C170.931 88.3364 174.871 84.3965 174.871 79.5365V9.13686C174.871 4.27679 170.931 0.336914 166.071 0.336914L114.31 0.336914C110.976 0.337022 107.93 2.22047 106.439 5.20166L104.288 9.50353L76.788 9.50353L74.6367 5.20166Z"
            fill={fillDark}
            stroke="#1F1F1F"
            strokeWidth="0.673077"
            strokeLinejoin="round"
          />
          <ellipse
            opacity="0.5"
            cx="45.8041"
            cy="44.3369"
            rx="26.3998"
            ry="30.7998"
            fill={fillDark}
            stroke="white"
            strokeOpacity="0.1"
            strokeWidth="0.673077"
          />
          <path
            d="M42.3223 28.7497C42.1453 27.9573 41.6765 27.2606 41.0092 26.7982L35.9444 23.2888C34.7963 22.4932 33.2508 22.5097 32.2316 23.465C27.6945 27.7176 24.7046 33.9751 23.9387 40.826C23.8545 41.5794 24.5822 42.146 25.3039 41.9139L29.6197 40.5258C29.8095 40.4648 30.0126 40.4576 30.2063 40.5052L45.8026 44.3357L42.3223 28.7497Z"
            fill={fillMedium}
          />
          <path
            d="M33.1086 48.417C32.2653 48.6881 31.5703 49.2936 31.1862 50.0918L27.8579 57.0087C27.4143 57.9307 27.4229 59.0155 27.9528 59.8908C31.4667 65.6945 36.8262 69.5473 42.727 70.5474C43.4454 70.6692 44.0247 70.0097 43.8659 69.2986L42.5966 63.6136C42.5661 63.477 42.5625 63.3358 42.586 63.1979L45.8026 44.3357L33.1086 48.417Z"
            fill={fillMedium}
          />
          <path
            d="M61.986 48.145C61.7962 48.206 61.5931 48.2132 61.3995 48.1656L45.8026 44.3357L49.2829 59.9212C49.4598 60.7135 49.9286 61.4102 50.5959 61.8726L55.6607 65.382C56.8089 66.1776 58.3544 66.1611 59.3736 65.2058C63.9108 60.9532 66.901 54.6958 67.667 47.8448C67.7512 47.0914 67.0235 46.5247 66.3018 46.7569L61.986 48.145Z"
            fill={fillMedium}
          />
          <path
            d="M63.7478 31.662C64.1914 30.7401 64.1829 29.6552 63.6529 28.78C60.1389 22.9763 54.7791 19.1235 48.8782 18.1233C48.1598 18.0016 47.5805 18.661 47.7392 19.3722L49.0086 25.0572C49.0391 25.1938 49.0427 25.335 49.0191 25.4729L45.8026 44.3357L58.4972 40.2538C59.3404 39.9827 60.0354 39.3772 60.4195 38.579L63.7478 31.662Z"
            fill={fillMedium}
          />
          <path
            d="M45.8026 44.3357L30.2063 40.5052C30.0126 40.4576 29.8095 40.4648 29.6197 40.5258L25.3039 41.9139C24.5822 42.146 23.8545 41.5794 23.9387 40.826C24.7046 33.9751 27.6945 27.7176 32.2316 23.465C33.2508 22.5097 34.7963 22.4932 35.9444 23.2888L41.0092 26.7982C41.6765 27.2606 42.1453 27.9573 42.3223 28.7497L45.8026 44.3357ZM45.8026 44.3357L42.586 63.1979C42.5625 63.3358 42.5661 63.477 42.5966 63.6136L43.8659 69.2986C44.0247 70.0097 43.4454 70.6692 42.727 70.5474C36.8262 69.5473 31.4667 65.6945 27.9528 59.8908C27.4229 59.0155 27.4143 57.9307 27.8579 57.0087L31.1862 50.0918C31.5703 49.2936 32.2653 48.6881 33.1086 48.417L45.8026 44.3357ZM45.8026 44.3357L61.3995 48.1656C61.5931 48.2132 61.7962 48.206 61.986 48.145L66.3018 46.7569C67.0235 46.5248 67.7512 47.0914 67.667 47.8448C66.901 54.6958 63.9108 60.9532 59.3736 65.2058C58.3544 66.1611 56.8089 66.1776 55.6607 65.382L50.5959 61.8726C49.9286 61.4102 49.4598 60.7135 49.2829 59.9212L45.8026 44.3357ZM45.8026 44.3357L49.0191 25.4729C49.0427 25.335 49.0391 25.1938 49.0086 25.0572L47.7392 19.3722C47.5805 18.661 48.1598 18.0016 48.8782 18.1233C54.7791 19.1235 60.1389 22.9763 63.6529 28.78C64.1829 29.6552 64.1914 30.7401 63.7478 31.662L60.4195 38.579C60.0354 39.3772 59.3404 39.9827 58.4972 40.2538L45.8026 44.3357Z"
            stroke="#1F1F1F"
            strokeWidth="0.673077"
          />
          <ellipse
            opacity="0.5"
            cx="135.27"
            cy="44.3384"
            rx="26.3998"
            ry="30.7998"
            fill={fillDark}
            stroke="white"
            strokeOpacity="0.1"
            strokeWidth="0.673077"
          />
          <path
            d="M131.789 28.7555C131.612 27.9631 131.143 27.2664 130.476 26.804L125.411 23.2946C124.263 22.499 122.718 22.5156 121.698 23.4708C117.161 27.7235 114.171 33.9809 113.406 40.8318C113.321 41.5852 114.049 42.1519 114.771 41.9198L119.086 40.5317C119.276 40.4706 119.479 40.4635 119.673 40.511L135.269 44.3416L131.789 28.7555Z"
            fill={fillMedium}
          />
          <path
            d="M122.575 48.4228C121.732 48.694 121.037 49.2995 120.653 50.0977L117.325 57.0146C116.881 57.9366 116.89 59.0214 117.42 59.8966C120.934 65.7003 126.293 69.5531 132.194 70.5533C132.912 70.675 133.491 70.0156 133.333 69.3045L132.063 63.6194C132.033 63.4829 132.029 63.3417 132.053 63.2037L135.269 44.3416L122.575 48.4228Z"
            fill={fillMedium}
          />
          <path
            d="M151.453 48.1508C151.263 48.2119 151.06 48.219 150.866 48.1715L135.269 44.3416L138.75 59.927C138.927 60.7194 139.395 61.4161 140.063 61.8785L145.128 65.3879C146.276 66.1835 147.821 66.167 148.84 65.2117C153.378 60.9591 156.368 54.7016 157.134 47.8507C157.218 47.0973 156.49 46.5306 155.769 46.7627L151.453 48.1508Z"
            fill={fillMedium}
          />
          <path
            d="M153.215 31.6679C153.658 30.7459 153.65 29.6611 153.12 28.7859C149.606 22.9821 144.246 19.1293 138.345 18.1292C137.627 18.0074 137.047 18.6669 137.206 19.378L138.475 25.0631C138.506 25.1996 138.509 25.3408 138.486 25.4788L135.269 44.3416L147.964 40.2597C148.807 39.9885 149.502 39.3831 149.886 38.5849L153.215 31.6679Z"
            fill={fillMedium}
          />
          <path
            d="M135.269 44.3416L119.673 40.511C119.479 40.4635 119.276 40.4706 119.086 40.5317L114.771 41.9198C114.049 42.1519 113.321 41.5852 113.406 40.8318C114.171 33.9809 117.161 27.7235 121.698 23.4708C122.718 22.5156 124.263 22.499 125.411 23.2946L130.476 26.804C131.143 27.2664 131.612 27.9631 131.789 28.7555L135.269 44.3416ZM135.269 44.3416L132.053 63.2037C132.029 63.3417 132.033 63.4829 132.063 63.6194L133.333 69.3045C133.491 70.0156 132.912 70.675 132.194 70.5533C126.293 69.5531 120.934 65.7003 117.42 59.8966C116.89 59.0214 116.881 57.9366 117.325 57.0146L120.653 50.0977C121.037 49.2995 121.732 48.694 122.575 48.4228L135.269 44.3416ZM135.269 44.3416L150.866 48.1715C151.06 48.219 151.263 48.2119 151.453 48.1508L155.769 46.7627C156.49 46.5306 157.218 47.0973 157.134 47.8507C156.368 54.7016 153.378 60.9591 148.84 65.2117C147.821 66.167 146.276 66.1835 145.128 65.3879L140.063 61.8785C139.395 61.4161 138.927 60.7194 138.75 59.927L135.269 44.3416ZM135.269 44.3416L138.486 25.4788C138.509 25.3408 138.506 25.1996 138.475 25.0631L137.206 19.378C137.047 18.6669 137.627 18.0074 138.345 18.1292C144.246 19.1293 149.606 22.9821 153.12 28.7859C153.65 29.6611 153.658 30.7459 153.215 31.6679L149.886 38.5849C149.502 39.3831 148.807 39.9885 147.964 40.2597L135.269 44.3416Z"
            stroke="#1F1F1F"
            strokeWidth="0.673077"
          />
          <path
            opacity="0.5"
            d="M74.4951 4.91895L87.6154 31.1595C87.7978 31.5244 88.1707 31.7548 88.5786 31.7548L92.4948 31.7548C92.9027 31.7548 93.2716 31.5323 93.454 31.1674C95.6195 26.8365 106.578 4.91895 106.578 4.91895M106.578 83.7518C106.578 83.7518 95.6195 61.8343 93.454 57.5033C93.2716 57.1385 92.9027 56.916 92.4948 56.916L88.5786 56.916C88.1707 56.916 87.7978 57.1464 87.6154 57.5113L74.4951 83.7518"
            stroke="#1F1F1F"
            strokeWidth="0.673077"
          />
          <rect
            x="180.738"
            y="9.13428"
            width="11.7333"
            height="70.3996"
            rx="0.807692"
            fill={fillDark}
            stroke="#1F1F1F"
            strokeWidth="0.673077"
          />
          <rect
            x="189.539"
            y="9.13135"
            width="2.93332"
            height="70.3996"
            rx="0.807692"
            fill={fillMedium}
            stroke="#1F1F1F"
            strokeWidth="0.673077"
          />
        </svg>
        <div className="flex flex-col items-center justify-center gap-4">
          <div className="flex flex-col items-center justify-center gap-3">
            <div className="title-h4 text-center text-foreground">Empty Inventory</div>
            <div className="subtext-medium text-center text-gray-dark-2200">
              Don&apos;t see what you&apos;re looking for?
            </div>
          </div>
          <div className="flex flex-row items-center justify-center gap-2">
            <Button
              onClick={() => {
                setIsModalOpen(true);
              }}
              variant="primary"
              className="label-medium rounded-[6px] px-3 py-[10px] leading-5"
            >
              Request Devices
            </Button>
            <Button
              onClick={() => {
                trackEvent(AnalyticsEventType.SHOW_ALL_DEVICES_CLICKED);
                onResetFilters();
              }}
              variant="outline"
              className="label-medium rounded-[6px] px-3 py-[10px] leading-5"
            >
              Show All Devices
            </Button>
          </div>
        </div>
      </div>
      {isModalOpen && (
        <RequestManagedCluster
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          selectedFilters={{
            hardwareNames: selectedHardwareNames,
            connectivityTiers: [4],
            hardwareQuantity: [1],
            regions:
              selectedFilters?.regions?.map((region) =>
                region === "LATAM America" ? "LATAM" : region
              ) || INVENTORY_REGIONS,
            confidentialCompute: selectedFilters?.confidential_compute || []
          }}
          additionalFilters={{ hardware_names: selectedHardwareNames }}
          title="Device Request"
          description={description}
          hideConnectivityTier={true}
          onSubmitOverride={async (values: RequestManagedClusterFormType) => {
            // Format regions - ensure they match HubSpot expected values
            const formattedRegions = values.regions.map((region) => {
              // Map any variations to standard values
              if (region === "LATAM America") return "LATAM";
              return region;
            });

            // Get hardware name (join multiple if selected)
            const hardwareName =
              values.hardwareNames.length > 0
                ? values.hardwareNames.join(", ")
                : selectedHardwareNames.join(", ");

            // Format date if provided
            const requiredBy = values.neededBy
              ? values.neededBy.toISOString().split("T")[0]
              : undefined;

            return await submitHardwareRequestToHubSpot({
              regions: formattedRegions,
              hardwareName,
              confidentialCompute: values.confidentialCompute || false,
              numberOfGpus: values.gpuQuantity || 1,
              contactEmail: values.contactEmail,
              clusterType: getClusterTypeDisplayName(clusterType), // Hidden field to identify cluster type
              telegramHandle: values.telegramHandle,
              requiredBy
            });
          }}
        />
      )}
    </>
  );
};
